import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../Modal";
import { sanitizeParagraphHtml } from "./paragraphHtml";
import { ParagraphRichEditor } from "./ParagraphRichEditor";

const BOLD_COLOR = "#005CA9";
const PARAGRAPH_MODAL_CLASS = "flex min-h-[75vh] max-h-[85vh] w-full max-w-3xl flex-col";

type ParagraphRichEditorModalProps = {
  show: boolean;
  value: string;
  onSave: (html: string) => void;
  onCancel: () => void;
  title?: string;
};

/** Rich text editor modal for an individual paragraph with bold and underline formatting. */
export const ParagraphRichEditorModal: React.FC<ParagraphRichEditorModalProps> = ({
  show,
  value,
  onSave,
  onCancel,
  title = "Edit paragraph",
}) => {
  const [local, setLocal] = useState("");

  useEffect(() => {
    if (show) setLocal(typeof value === "string" ? value : "");
  }, [show, value]);

  const handleSave = useCallback(() => {
    onSave(sanitizeParagraphHtml(local));
    onCancel();
  }, [local, onSave, onCancel]);

  if (!show) return null;

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered dialogClassName={PARAGRAPH_MODAL_CLASS}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="flex flex-1 flex-col min-h-0">
        <p className="mb-3 text-xs text-slate-500">
          Use the toolbar for <strong style={{ color: BOLD_COLOR }}>bold</strong> and underline. One blank line is
          allowed between lines.
        </p>
        <ParagraphRichEditor
          value={local}
          onChange={setLocal}
          minHeight="140px"
          placeholder="Type here…"
          className="flex-1 min-h-0 flex flex-col"
        />
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-[#005CA9] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};
